/**
 * Regras de carimbo da bolha otimista — conserto do bug "mensagens fora de
 * ordem quando algum relógio está torto" (PC do atendente atrasado OU
 * adiantado, e até celular do cliente torto).
 *
 * Dois casos MEDIDOS em produção/lab pelo rastro `?trace=1` (2026-07-24):
 *
 * CASO 1 — relógio ATRASADO (lab, aba ~2min atrás):
 *   Msg do cliente carrega a hora do WHATSAPP (15:13:11). A bolha otimista
 *   nascia com o relógio da ABA (15:11:08) → ordenação por `created_at` a
 *   colocava ACIMA da msg do cliente.
 *
 * CASO 2 — relógio ADIANTADO (produção srv01, aba do atendente ~1min22s à
 *   frente): a bolha nasceu 16:32:20 (aba); o servidor gravou 16:30:57. A
 *   regra antiga de promoção ("nunca regride: max(atual, servidor)") RECUSOU
 *   a correção e a bolha ficou carimbada no futuro → a resposta seguinte do
 *   cliente (hora certa, ~16:31) entrava ACIMA da bolha do atendente.
 *
 * As duas defesas, na ordem em que atuam:
 *
 * `carimboDoNascimento` — a bolha nasce em `max(agora, mais nova + 1ms)`.
 *   Relógio certo → "agora" (comportamento de sempre). Atrasado → logo depois
 *   da mensagem mais nova. Quem acabou de enviar SEMPRE fica no fim.
 *
 * `carimboDaPromocao` — quando o carimbo do servidor chega (resposta do POST
 *   ou eco do socket), a bolha ADOTA o valor do servidor com o MESMO piso do
 *   nascimento: `max(servidor, mais nova + 1ms)` — onde "mais nova" EXCLUI a
 *   própria bolha. Cobre os quatro mundos:
 *   - aba atrasada  → servidor é maior → adota a verdade (valor exibido certo)
 *   - aba adiantada → servidor é menor mas ainda maior que a última msg →
 *     adota a verdade e a bolha VOLTA do futuro (caso 2)
 *   - servidor atrás da msg do cliente (lab com PC inteiro atrasado, OU
 *     celular do cliente adiantado em produção) → piso segura a bolha logo
 *     depois da msg do cliente — nunca inverte a ordem local
 */

function ms(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/** Carimbo com que a bolha otimista nasce: `max(agora, mais nova + 1ms)`. */
export function carimboDoNascimento(
  agoraIso: string,
  maisNovaIso: string | null | undefined,
): string {
  const agora = ms(agoraIso);
  const maisNova = ms(maisNovaIso);
  if (agora === null || maisNova === null) return agoraIso;
  if (agora > maisNova) return agoraIso;
  return new Date(maisNova + 1).toISOString();
}

/**
 * Carimbo ao promover a bolha com a resposta do servidor (POST ou eco):
 * adota o valor do servidor com piso — `max(servidor, mais nova + 1ms)`.
 * `maisNovaIso` deve EXCLUIR a própria bolha sendo promovida.
 * Servidor ausente/inválido → mantém o atual.
 */
export function carimboDaPromocao(
  atualIso: string,
  servidorIso: string | null | undefined,
  maisNovaIso: string | null | undefined,
): string {
  if (ms(servidorIso) === null) return atualIso;
  return carimboDoNascimento(servidorIso as string, maisNovaIso);
}

/**
 * A mensagem mais nova do cache (maior `created_at`), pra ancorar o piso.
 * `exceto` exclui ids do cálculo — na promoção, a própria bolha (temp e real)
 * não pode ser a âncora de si mesma.
 */
export function maisNovaDoCache(
  paginas: Array<{ messages: Array<{ id: number; created_at: string }> }>,
  exceto?: ReadonlySet<number>,
): { created_at: string } | null {
  let maisNova: { created_at: string } | null = null;
  for (const p of paginas) {
    for (const m of p.messages) {
      if (exceto?.has(m.id)) continue;
      if (maisNova === null || m.created_at.localeCompare(maisNova.created_at) > 0) {
        maisNova = m;
      }
    }
  }
  return maisNova;
}
